/**
 * レセプト内容一覧 — search form.
 *
 * Layout follows the customer's mock-up (UAT No203):
 *   Row 1: 提出年月 / はり・きゅう師 (or マッサージ師) / 施術年月
 *   Row 2: radio 保険種別 ({insurer_type_id}) | radio 保険者名 ({insurer_name}) | 仮名頭文字
 *   Row 3: レセプト種別 radio | 並び順 radio | 検索 button | 該当件数 badge
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';

import { AppButton, AppRadio, AppRadioGroup, AppSelect, AppTextField } from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import { JA_JP } from '@/components/organisms/RA2NDatePicker/constants';
import { useRezeptHistoriesMassagers } from '@/hooks/main/useRezeptHistoriesMassagers';
import { convGtJMonth } from '@/utils/dateConverter';

import {
  REZEPT_KBN_MAIN,
  REZEPT_KBN_RELATED,
  SEARCH_RADIO_INSURER_NAME,
  SEARCH_RADIO_INSURER_TYPE,
  SORT_BY_INSURER_NUMBER,
  SORT_BY_KANA,
  SORT_BY_SERIAL,
  VISITMONTH_FORMAT_MESSAGE,
  YEARMONTH_FORMAT_MESSAGE,
  YEARMONTH_REQUIRED_MESSAGE,
} from '../constants';
import type {
  IniOption,
  InsurerTypeOption,
  RezeptHistoriesFormValues,
  RezeptKbn,
  SearchRadio,
  SortRadio,
} from '../types';

const monthFormat = parseFormat(JA_JP.formatMonth);

/** Wareki month display ("令和8年03月") → ISO "YYYY-MM". Empty string if unparseable. */
function warekiMonthToIso(value: string): string {
  if (!value?.trim()) return '';
  const d = parseDate(value, monthFormat);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  return `${y}-${String(m).padStart(2, '0')}`;
}

/** ISO "YYYY-MM" → wareki month display (e.g. "令和06年04月"). */
function isoMonthToWareki(iso: string | null | undefined): string {
  if (!iso) return '';
  const trimmed = iso.trim();
  if (!trimmed) return '';
  const d = dayjs(`${trimmed}-01`);
  return d.isValid() ? convGtJMonth(d) : '';
}

interface Props {
  initialValues: RezeptHistoriesFormValues;
  insurerTypes: InsurerTypeOption[];
  iniOptions: IniOption[];
  massagerLabel: string; // 'はり・きゅう師' (A2) or 'マッサージ師' (R2)
  totalCount: number;
  isSearching: boolean;
  onSearch: (form: RezeptHistoriesFormValues) => void;
  onEmptyYearmonth: () => void;
  onInvalidVisitmonth: () => void;
  onInvalidYearmonth: () => void;
}

const RezeptHistoriesSearchForm: React.FC<Props> = ({
  initialValues,
  insurerTypes,
  iniOptions,
  massagerLabel,
  totalCount,
  isSearching,
  onSearch,
  onEmptyYearmonth,
  onInvalidVisitmonth,
  onInvalidYearmonth,
}) => {
  const [form, setForm] = useState<RezeptHistoriesFormValues>(initialValues);

  // Keep wareki strings in state to feed RA2NDatePicker; sync both ways to ISO.
  const [searchYearmonthWareki, setSearchYearmonthWareki] = useState<string>(() =>
    isoMonthToWareki(initialValues.search_yearmonth)
  );
  const [searchVisitmonthWareki, setSearchVisitmonthWareki] = useState<string>(() =>
    isoMonthToWareki(initialValues.search_visitmonth ?? '')
  );

  // Reset when initialValues change (service-type switch).
  useEffect(() => {
    setForm(initialValues);
    setSearchYearmonthWareki(isoMonthToWareki(initialValues.search_yearmonth));
    setSearchVisitmonthWareki(isoMonthToWareki(initialValues.search_visitmonth ?? ''));
  }, [initialValues]);

  // Dependent masseur dropdown (reloads on 提出年月 change).
  const { data: massagers } = useRezeptHistoriesMassagers(form.search_yearmonth || null);

  const insurerTypeOptions = useMemo(
    () =>
      insurerTypes
        // Per legacy blade: hide id=1 ("無し") from the dropdown.
        .filter(t => t.id !== 1)
        .map(t => ({ label: t.name, value: t.id })),
    [insurerTypes]
  );

  const iniSelectOptions = useMemo(
    () => [
      { label: 'すべて', value: '' },
      ...iniOptions.map(i => ({ label: i.name, value: i.id })),
    ],
    [iniOptions]
  );

  const massagerOptions = useMemo(
    () => [
      { label: 'すべて', value: '' },
      ...massagers.map(m => ({
        label: `${m.serial_number}:${m.name}`,
        value: String(m.massager_id),
      })),
    ],
    [massagers]
  );

  const isInsurerTypeRadio = form.search_radio === SEARCH_RADIO_INSURER_TYPE;
  const isInsurerNameRadio = form.search_radio === SEARCH_RADIO_INSURER_NAME;

  const handleSearchRadioChange = useCallback((value: SearchRadio) => {
    setForm(prev => {
      if (value === SEARCH_RADIO_INSURER_NAME) {
        // Clear 保険種別. 仮名頭文字 is about the patient, so it stays (UAT No207).
        return { ...prev, search_radio: value, insurer_type_id: null };
      }
      // SEARCH_RADIO_INSURER_TYPE: clear 保険者名 block
      return { ...prev, search_radio: value, insurer_name: '' };
    });
  }, []);

  const handleYearmonthChange = useCallback((warekiStr: string) => {
    setSearchYearmonthWareki(warekiStr);
    const iso = warekiMonthToIso(warekiStr);
    setForm(prev => ({ ...prev, search_yearmonth: iso, massager_id: null }));
  }, []);

  const handleVisitmonthChange = useCallback((warekiStr: string) => {
    setSearchVisitmonthWareki(warekiStr);
    const iso = warekiMonthToIso(warekiStr);
    setForm(prev => ({ ...prev, search_visitmonth: iso }));
  }, []);

  const handleSubmit = useCallback(() => {
    // BD: empty 提出年月 → "【提出年月】を入力してください"; invalid format → "【提出年月】を正しく入力してください".
    if (!form.search_yearmonth) {
      // Distinguish empty input vs unparseable input by checking the wareki display string.
      if (searchYearmonthWareki.trim()) {
        onInvalidYearmonth();
      } else {
        onEmptyYearmonth();
      }
      return;
    }
    // 施術年月 is optional; only validate format if user typed something.
    if (searchVisitmonthWareki.trim() && !form.search_visitmonth) {
      onInvalidVisitmonth();
      return;
    }
    onSearch(form);
  }, [
    form,
    searchYearmonthWareki,
    searchVisitmonthWareki,
    onSearch,
    onEmptyYearmonth,
    onInvalidYearmonth,
    onInvalidVisitmonth,
  ]);

  return (
    <div className="flex flex-wrap items-end justify-end gap-4">
      {/* UAT No203: one grid for all rows, so label and input columns line up top to bottom. */}
      <AppRadioGroup
        className="!grid items-center grow grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-4 lg:grid-cols-[auto_auto_auto_auto_auto_auto] lg:gap-x-3 lg:gap-y-5"
        value={form.search_radio}
        onChange={e => handleSearchRadioChange(e.target.value as SearchRadio)}
      >
        {/* Row 1: 提出年月 / マッサージ師 / 施術年月 */}
        <div className="flex items-center justify-between gap-2 text-sm text-gray-600 whitespace-nowrap">
          <span className="cursor-text">提出年月</span>
          <span className="px-1.5 py-0.5 text-xs bg-pink-500 text-white rounded">必須</span>
        </div>
        <RA2NDatePicker
          picker="month"
          className="w-40"
          value={searchYearmonthWareki}
          onChange={handleYearmonthChange}
          placeholder="元号00年00月"
        />

        <span className="text-sm text-gray-600 whitespace-nowrap cursor-text lg:ml-8">
          {massagerLabel}
        </span>
        <AppSelect
          id="rh-massager"
          className="w-40 lg:w-44"
          value={form.massager_id != null ? String(form.massager_id) : ''}
          options={massagerOptions}
          onChange={(value: unknown) => {
            const v = (value as string) ?? '';
            setForm(prev => ({ ...prev, massager_id: v === '' ? null : Number(v) }));
          }}
        />

        <span className="text-sm text-gray-600 whitespace-nowrap cursor-text lg:ml-8">
          施術年月
        </span>
        <RA2NDatePicker
          picker="month"
          className="w-40"
          value={searchVisitmonthWareki}
          onChange={handleVisitmonthChange}
          placeholder="元号00年00月"
        />

        {/* Row 2: 保険種別 / 保険者名 / 仮名頭文字 */}
        <AppRadio value={SEARCH_RADIO_INSURER_TYPE} className="whitespace-nowrap">
          保険種別
        </AppRadio>
        <AppSelect
          className="w-40"
          value={form.insurer_type_id != null ? String(form.insurer_type_id) : ''}
          options={[
            { label: 'すべて', value: '' },
            ...insurerTypeOptions.map(o => ({ label: o.label, value: String(o.value) })),
          ]}
          disabled={!isInsurerTypeRadio}
          onChange={(value: unknown) => {
            const v = (value as string) ?? '';
            setForm(prev => ({
              ...prev,
              insurer_type_id: v === '' ? null : Number(v),
            }));
          }}
        />

        <AppRadio value={SEARCH_RADIO_INSURER_NAME} className="whitespace-nowrap lg:ml-8">
          保険者名
        </AppRadio>
        <AppTextField
          className="w-40 lg:w-44"
          value={form.insurer_name}
          disabled={!isInsurerNameRadio}
          maxLength={255}
          onChange={e => setForm(prev => ({ ...prev, insurer_name: e.target.value }))}
        />

        <span className="text-sm text-gray-600 whitespace-nowrap cursor-text lg:ml-8">
          仮名頭文字
        </span>
        <AppSelect
          className="w-40"
          value={form.ini}
          options={iniSelectOptions}
          onChange={(value: unknown) =>
            setForm(prev => ({ ...prev, ini: (value as string) ?? '' }))
          }
        />

        {/* Row 3: レセプト種別 / 並び順 */}
        <span className="text-sm text-gray-600 whitespace-nowrap cursor-text">レセプト種別</span>
        <div className="inline-flex items-center justify-self-start p-2 border border-gray-300 rounded gap-x-3 lg:px-3">
          <AppRadioGroup
            className="flex flex-wrap gap-x-3 gap-y-1"
            value={form.rezept_kbn}
            onChange={e => setForm(prev => ({ ...prev, rezept_kbn: e.target.value as RezeptKbn }))}
          >
            <AppRadio value={REZEPT_KBN_MAIN} className="whitespace-nowrap">
              本レセ
            </AppRadio>
            <AppRadio value={REZEPT_KBN_RELATED} className="whitespace-nowrap">
              関レセ
            </AppRadio>
          </AppRadioGroup>
        </div>

        <span className="text-sm text-gray-600 whitespace-nowrap cursor-text lg:ml-8">並び順</span>
        <div className="inline-flex items-center justify-self-start p-2 border border-gray-300 rounded gap-x-3 lg:col-span-3 lg:px-3">
          <AppRadioGroup
            className="flex flex-wrap gap-x-3 gap-y-1"
            value={form.sort_radio}
            onChange={e => setForm(prev => ({ ...prev, sort_radio: e.target.value as SortRadio }))}
          >
            <AppRadio value={SORT_BY_SERIAL} className="whitespace-nowrap">
              受診者番号
            </AppRadio>
            <AppRadio value={SORT_BY_KANA} className="whitespace-nowrap">
              仮名
            </AppRadio>
            <AppRadio value={SORT_BY_INSURER_NUMBER} className="whitespace-nowrap">
              保険者番号
            </AppRadio>
          </AppRadioGroup>
        </div>
      </AppRadioGroup>

      <div className="flex flex-wrap items-center justify-end gap-3 lg:gap-4">
        <AppButton type="primary" onClick={handleSubmit} loading={isSearching}>
          <i className="mr-1 fa fa-search" aria-hidden="true" /> 検索
        </AppButton>
        <div className="px-3 py-1 text-sm font-medium leading-tight text-center text-black rounded-md bg-amber-400 whitespace-nowrap cursor-text">
          該当件数 {totalCount} 件
        </div>
      </div>
    </div>
  );
};

// Re-export messages used by the parent page so it can call toast.error consistently.
export { YEARMONTH_REQUIRED_MESSAGE, YEARMONTH_FORMAT_MESSAGE, VISITMONTH_FORMAT_MESSAGE };

export default RezeptHistoriesSearchForm;
